import { Reveal } from "@/components/Reveal";
import { QRCodeSVG } from "qrcode.react";
import { APP_DOWNLOAD_URL } from "@/lib/brand";
import { QrCode, Link2, Share2, Check, UserCog, Users } from "lucide-react";

// Real, scannable QR that deep-links to the app download.
function QrGraphic({ size = 150 }) {
  return (
    <QRCodeSVG
      value={APP_DOWNLOAD_URL}
      size={size}
      level="M"
      marginSize={1}
      bgColor="#ffffff"
      fgColor="#2B2B2B"
      aria-label="Scan to download DriveAssure"
    />
  );
}

const STEPS = [
  { icon: QrCode, title: "Instructor shares a code", desc: "Generate a one-tap Family Link code and QR from your dashboard." },
  { icon: Share2, title: "Learner or parent scans", desc: "They scan the QR or enter the code when they register — free, no setup." },
  { icon: Check, title: "Linked instantly", desc: "Progress reports and practice plans flow straight to the family." },
];

export default function FamilyLink() {
  return (
    <section id="family-link" data-testid="family-link-section" className="bg-[#F7F7F7] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        {/* copy */}
        <div className="lg:col-span-6">
          <Reveal>
            <p className="overline text-[#2BBFB3]">Family Link</p>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Connect a learner in one scan.
            </h2>
            <p className="mt-6 max-w-lg text-lg text-[#2B2B2B]/65 leading-relaxed">
              Share a Family Link code or QR and your learners, parents and supervising drivers are linked
              to your school instantly — so everyone sees the same progress, for free.
            </p>
          </Reveal>
          <div className="mt-9 space-y-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={0.1 + i * 0.08}>
                <div className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-white border border-[#E5E5E5] text-[#1FA097] shrink-0">
                    <s.icon size={19} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-[#2B2B2B]">{s.title}</p>
                    <p className="mt-0.5 text-sm text-[#2B2B2B]/60 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* visual */}
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-md">
              {/* instructor card */}
              <div className="rounded-3xl border border-[#E5E5E5] bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-[#2B2B2B] text-[#2BBFB3]"><UserCog size={17} /></span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#2B2B2B]/40 font-semibold">Instructor</p>
                    <p className="font-display font-bold text-[#2B2B2B] leading-none">James's Driving School</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-col items-center rounded-2xl bg-[#F7F7F7] p-5">
                  <div className="rounded-xl bg-white p-3 ring-1 ring-[#E5E5E5]"><QrGraphic size={150} /></div>
                  <p className="mt-4 text-[11px] uppercase tracking-widest text-[#2B2B2B]/40 font-semibold">Family Link code</p>
                  <p className="font-display font-extrabold text-2xl tracking-[0.2em] text-[#2B2B2B]" data-testid="family-link-code">DA-4K7Q</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#2BBFB3]/12 px-3 py-1.5 text-xs font-semibold text-[#1FA097]">
                    <Share2 size={12} /> Share with family
                  </span>
                </div>
              </div>

              {/* connector */}
              <div className="relative flex justify-center py-1">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#2BBFB3] text-[#0f1211] ring-4 ring-[#F7F7F7] shadow -my-5 z-10">
                  <Link2 size={18} />
                </span>
              </div>

              {/* learner / parent card */}
              <div className="rounded-3xl border border-[#2BBFB3]/40 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#2BBFB3]/15 text-[#1FA097]"><Users size={17} /></span>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#2B2B2B]/40 font-semibold">Learner &amp; parent</p>
                      <p className="font-display font-bold text-[#2B2B2B] leading-none">Connected</p>
                    </div>
                  </div>
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-[#2BBFB3] text-[#0f1211]"><Check size={15} strokeWidth={3} /></span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {["Progress", "Plans", "Reports"].map((t) => (
                    <div key={t} className="rounded-lg bg-[#F7F7F7] py-2 text-center text-[11px] font-semibold text-[#2B2B2B]/60">{t}</div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
